<template>
  <div>
    <b-row>
      <b-col
        v-for="(item, index) in protestos"
        :key="item.title"
        cols="auto"
        class="ml-2 cursor-pointer"
        @click="selectTable(index)"
      >
        <b-media>
          <template #aside>
            <b-avatar
              size="45"
              :variant="`light-${resolveStatusVariantAndIcon(item.title).variant
              }`"
            >
              <feather-icon :icon="resolveStatusVariantAndIcon(item.title).icon" />
            </b-avatar>
          </template>
          <b-media-body>
            <h4 class="font-weight-bolder mb-0">
              {{ item.items.length }}
            </h4>
            <b-card-text class="font-small-3 mb-0">
              {{ item.title }}
            </b-card-text>
          </b-media-body>
        </b-media>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-card>
          <b-row class="mb-2">
            <b-col lg="4">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-primary"
                @click="showFilterModal"
              >
                <feather-icon icon="FilterIcon" />
                Filtrar
              </b-button>
            </b-col>
            <b-col
              lg="4"
              class="d-flex justify-content-center"
            >
              <b-button
                variant="success"
                :disabled="exporting"
                @click="exportToExcel"
              >
                <feather-icon
                  icon="FileTextIcon"
                  class="mr-50"
                />
                {{ exporting ? 'Exportando...' : 'Exportar para Excel' }}
              </b-button>
            </b-col>
            <b-col
              lg="4"
              class="d-flex justify-content-end"
            >
              <b-form-input
                v-model="filter"
                type="search"
                placeholder="Pesquisar"
                debounce="800"
                size="sm"
              />
            </b-col>
          </b-row>
          <b-table
            :fields="fields"
            :items="protestos[table].items"
            :filter="filter"
            :current-page="currentPage"
            :per-page="perPage"
            striped
            hover
            responsive
            @filtered="onFiltered"
          >
            <template #cell(cliente_id)="data">
              {{ data.item.cliente ? data.item.cliente.id : '' }}
            </template>

            <template #cell(credor)="data">
              {{ data.item.credor }}
            </template>

            <template #cell(cliente)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :text="getInitials(
                      data.item.cliente ? data.item.cliente.nome : '',
                    )
                    "
                    :variant="`light-${resolveClientAvatarVariant(
                      data.item.classificacao,
                    )}`"
                  />
                </template>
                <span class="font-weight-bold d-block text-nowrap">
                  {{ data.item.cliente ? data.item.cliente.nome : '' }}
                </span>
              </b-media>
            </template>

            <template #cell(classificacao)="data">
              <b-badge :variant="getClassificacaoVariant(data.item.classificacao)">
                {{ getClassificacaoText(data.item.classificacao) }}
              </b-badge>
            </template>

            <template #cell(data_acordo)="data">
              {{ dataHora(data.item.data_acordo) }}
            </template>

            <template #cell(valor)="data">
              {{
                data.item.valor
                  ? valorBr(parseFloat(data.item.valor), true)
                  : ''
              }}
            </template>

            <template #cell(actions)="data">
              <b-row>
                <b-col>
                  <b-avatar
                    :id="`visualizar-acordo-${data.item.id}`"
                    class="mr-1 cursor-pointer"
                    size="32"
                    variant="light-warning"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      @click="visualizarAcordo(data.item)"
                    />
                  </b-avatar>
                </b-col>
              </b-row>
            </template>
          </b-table>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            size="sm"
            class="my-0"
          />
          <b-spinner
            v-if="loading"
            variant="primary"
            label="Carregando..."
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal de Filtro -->
    <b-modal
      ref="modalFiltro"
      title="Filtrar Protestos"
      ok-title="Aplicar"
      cancel-title="Cancelar"
      @ok="aplicarFiltro"
    >
      <b-form>
        <b-form-group
          label="Período"
          label-for="periodo"
        >
          <b-row>
            <b-col>
              <b-form-datepicker
                id="data-inicial"
                v-model="filtros.data_inicial"
                placeholder="Data Inicial"
                locale="pt-BR"
                :date-format-options="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }"
              />
            </b-col>
            <b-col>
              <b-form-datepicker
                id="data-final"
                v-model="filtros.data_final"
                placeholder="Data Final"
                locale="pt-BR"
                :date-format-options="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }"
              />
            </b-col>
          </b-row>
        </b-form-group>

        <b-form-group
          label="Classificação"
          label-for="classificacao"
        >
          <b-form-checkbox-group
            id="classificacao"
            v-model="filtros.classificacao"
            :options="classificacaoOptions"
          />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BTable,
  BMedia,
  BMediaBody,
  BAvatar,
  BPagination,
  BFormInput,
  BModal,
  BForm,
  BFormGroup,
  BFormDatepicker,
  BFormCheckboxGroup,
  BBadge,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from '@/../axios-auth'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BTable,
    BMedia,
    BMediaBody,
    BAvatar,
    BPagination,
    BFormInput,
    BModal,
    BForm,
    BFormGroup,
    BFormDatepicker,
    BFormCheckboxGroup,
    BBadge,
    BButton,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    updateContador: {
      type: Function,
      required: false,
      default: () => { },
    },
  },
  data() {
    return {
      filter: '',
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      table: 0,
      loading: false,
      dataLoaded: false,
      exporting: false,
      protestos: [
        {
          title: 'Todos',
          items: [],
        },
        {
          title: 'Protestando',
          items: [],
        },
        {
          title: 'Protestado',
          items: [],
        },
        {
          title: 'Protesto Renegociado',
          items: [],
        },
        {
          title: 'Concluído',
          items: [],
        },
      ],
      fields: [
        { key: 'cliente_id', label: 'ID' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'credor', label: 'Credor' },
        { key: 'classificacao', label: 'Classificação' },
        { key: 'data_acordo', label: 'Data do Acordo' },
        { key: 'valor', label: 'Valor Nominal' },
        { key: 'valor_divida', label: 'Valor Dívida' },
        { key: 'actions', label: 'Ações' },
      ],
      filtros: {
        data_inicial: `${new Date().getFullYear()}-01-01`,
        data_final: new Date().toISOString().substr(0, 10),
        classificacao: [1, 2, 3, 4],
      },
      classificacaoOptions: [
        { text: 'Protestando', value: 1 },
        { text: 'Protestado', value: 2 },
        { text: 'Protesto Renegociado', value: 3 },
        { text: 'Concluído', value: 4 },
      ],
    }
  },
  created() {
    this.getAcordos()
    // Será carregado quando o card for selecionado
  },
  methods: {
    getFirstDayOfMonth() {
      const date = new Date()
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0',
      )}-01`
    },
    getLastDayOfMonth() {
      const date = new Date()
      const lastDay = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0,
      ).getDate()
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0',
      )}-${lastDay}`
    },
    formatDateForApi(dateString) {
      if (!dateString) return ''
      return `${dateString} ${dateString === this.getFirstDayOfMonth() ? '00:00:00' : '23:59:59'
      }`
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    selectTable(value) {
      this.table = value
      this.totalRows = this.protestos[value].items.length
      if (!this.dataLoaded) {
        this.getAcordos()
      }
    },
    async getAcordos() {
      // Evitar chamadas duplicadas se os dados já foram carregados
      if (this.dataLoaded && this.protestos[0].items.length > 0) {
        return
      }

      this.loading = true
      try {
        const response = await axios.post('api/v1/acordo/filtroAcordo', {
          filtros: {
            data_inicial: this.formatDateForApi(this.filtros.data_inicial),
            data_final: this.formatDateForApi(this.filtros.data_final),
            classificacao: this.filtros.classificacao,
          },
        })

        // A resposta está aninhada em dados.filtro
        const acordos = response.data.dados.filtro || []

        // Limpar arrays
        this.protestos.forEach(categoria => {
          categoria.items = []
        })

        // Processar dados
        acordos.forEach(item => {
          // Extrair a primeira classificação (ou usar um valor padrão)
          const classificacao = item.classificacoes && item.classificacoes.length > 0
            ? item.classificacoes[0].id
            : 0

          // Calcular valor da dívida (valor nominal - juros - multa - honorário - desconto)
          const valorNominal = parseFloat(item.valor_nominal || 0)
          const juros = parseFloat(item.juros || 0)
          const multas = parseFloat(item.multas || 0)
          const honorarios = parseFloat(item.honorarios || 0)
          const desconto = parseFloat(item.desconto || 0)
          const valorDivida = valorNominal - juros - multas - honorarios - desconto

          // Criar objeto formatado para a tabela
          const acordoFormatado = {
            id: item.id,
            numero_acordo: item.numero_acordo,
            cliente: {
              id: item.cliente_id, // Adicionando o ID do cliente
              nome: item.cliente,
            },
            classificacao,
            data_acordo: item.created_at,
            valor: item.valor_nominal,
            valor_divida: valorDivida,
            status: item.status,
            credor: item.credor,
            quantidade_parcelas: item.quantidade_parcelas,
            forma_pagamento: item.forma_pagamento,
            data_vencimento: item.data_vencimento_ultima_parcela,
            classificacao_nome:
              item.classificacoes && item.classificacoes.length > 0
                ? item.classificacoes[0].nome
                : 'Não classificado',
            // Adicionando os campos que faltavam
            valor_nominal: item.valor_nominal,
            juros: item.juros,
            multas: item.multas,
            honorarios: item.honorarios,
            desconto: item.desconto,
          }

          // Adicionar ao array "Todos"
          this.protestos[0].items.push(acordoFormatado)

          // Adicionar ao array específico da classificação
          if (classificacao >= 1 && classificacao <= 4) {
            this.protestos[classificacao].items.push(acordoFormatado)
          }
        })

        this.totalRows = this.protestos[this.table].items.length
        this.dataLoaded = true

        // Atualizar contador se a função existir
        if (this.updateContador) {
          this.updateContador(acordos.length)
        }
      } catch (error) {
        this.$toast(
          {
            props: {
              title: 'Erro',
              icon: 'AlertTriangleIcon',
              text:
                error.response?.data?.message
                || 'Não foi possível buscar os acordos.',
              variant: 'danger',
            },
          },
          {
            position: 'top-center',
          },
        )
      } finally {
        this.loading = false
      }
    },
    resolveStatusVariantAndIcon(status) {
      if (status === 'Protestando') return { variant: 'primary', icon: 'AlertCircleIcon' }
      if (status === 'Protestado') return { variant: 'warning', icon: 'AlertTriangleIcon' }
      if (status === 'Protesto Renegociado') return { variant: 'danger', icon: 'XCircleIcon' }
      if (status === 'Concluído') return { variant: 'success', icon: 'CheckCircleIcon' }
      return { variant: 'info', icon: 'InfoIcon' } // Para "Todos"
    },
    resolveClientAvatarVariant(classificacao) {
      if (classificacao === 1) return 'primary'
      if (classificacao === 2) return 'warning'
      if (classificacao === 3) return 'danger'
      if (classificacao === 4) return 'success'
      return 'info'
    },
    getClassificacaoVariant(classificacao) {
      if (classificacao === 1) return 'primary'
      if (classificacao === 2) return 'warning'
      if (classificacao === 3) return 'danger'
      if (classificacao === 4) return 'success'
      return 'info'
    },
    getClassificacaoText(classificacao) {
      return (
        ['Protestando', 'Protestado', 'Protesto Renegociado', 'Concluído'][
          classificacao - 1
        ] || 'Não classificado'
      )
    },
    getInitials(name) {
      if (!name) return 'NA'
      return name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2)
    },
    dataHora(data) {
      return data ? new Date(data).toLocaleDateString('pt-BR') : ''
    },
    valorBr(valor, simbolo = false) {
      const formatado = new Intl.NumberFormat('pt-BR', {
        style: simbolo ? 'currency' : 'decimal',
        currency: 'BRL',
        minimumFractionDigits: 2,
      }).format(valor)
      return formatado
    },
    visualizarAcordo(item) {
      // Redirecionar para a página de negociação com o ID do cliente
      if (item && item.cliente && item.cliente.id) {
        this.$router.push(`/negociacao/${item.cliente.id}`)
      } else {
        this.$toast(
          {
            props: {
              title: 'Erro',
              icon: 'AlertTriangleIcon',
              text: 'ID do cliente não encontrado.',
              variant: 'danger',
            },
          },
          {
            position: 'top-center',
          },
        )
      }
    },
    showFilterModal() {
      this.$refs.modalFiltro.show()
    },
    aplicarFiltro() {
      this.dataLoaded = false
      this.getAcordos()
    },
    exportToExcel() {
      this.exporting = true
      this.getDataToExported()
        .then(file => {
          if (file) {
            saveAs(file)
          }
          this.exporting = false
        })
        .catch(() => {
          this.exporting = false
          this.$toast(
            {
              props: {
                title: 'Erro',
                icon: 'AlertTriangleIcon',
                text: 'Não foi possível exportar os dados.',
                variant: 'danger',
              },
            },
            {
              position: 'top-center',
            },
          )
        })
    },
    async getDataToExported() {
      const workbook = new ExcelJS.Workbook()
      workbook.creator = 'VOZ - COBRANÇA'
      workbook.created = new Date()
      const worksheet = workbook.addWorksheet('Protestos')
      const itemsPlan = this.protestos[this.table].items

      worksheet.columns = [
        { header: 'NÚMERO DO ACORDO', key: 'numero_acordo' },
        { header: 'CLIENTE ID', key: 'cliente_id' },
        { header: 'CLIENTE', key: 'cliente_nome' },
        { header: 'CREDOR', key: 'credor' },
        { header: 'CLASSIFICAÇÃO', key: 'classificacao' },
        { header: 'VALOR DÍVIDA', key: 'valor_divida' },
        { header: 'JUROS', key: 'juros' },
        { header: 'MULTAS', key: 'multas' },
        { header: 'HONORARIOS', key: 'honorarios' },
        { header: 'DESCONTO', key: 'desconto' },
        { header: 'VALOR NOMINAL', key: 'valor_nominal' },
        { header: 'STATUS', key: 'status' },
        { header: 'DATA DO ACORDO', key: 'data_acordo' },
        { header: 'DATA DE VENCIMENTO', key: 'data_vencimento' },
        { header: 'QUANTIDADE PARCELAS', key: 'quantidade_parcelas' },
      ]

      itemsPlan.forEach(item => {
        worksheet.addRow({
          numero_acordo: item.numero_acordo || '',
          cliente_id: item.cliente?.id || '',
          cliente_nome: item.cliente?.nome || '',
          classificacao: this.getClassificacaoText(item.classificacao) || '',
          valor: item.valor ? this.valorBr(parseFloat(item.valor), true) : '',
          status: item.status || '',
          data_acordo: this.dataHora(item.data_acordo) || '',
          valor_nominal: item.valor_nominal ? this.valorBr(parseFloat(item.valor_nominal), true) : '',
          valor_divida: item.valor_divida ? this.valorBr(parseFloat(item.valor_divida), true) : '',
          juros: item.juros ? this.valorBr(parseFloat(item.juros), true) : '',
          multas: item.multas ? this.valorBr(parseFloat(item.multas), true) : '',
          honorarios: item.honorarios ? this.valorBr(parseFloat(item.honorarios), true) : '',
          desconto: item.desconto ? this.valorBr(parseFloat(item.desconto), true) : '',
          data_vencimento: this.dataHora(item.data_vencimento) || '',
          credor: item.credor || '',
          quantidade_parcelas: item.quantidade_parcelas || '',
        })
      })

      // Estilização do cabeçalho
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (rowNumber === 1) {
            cell.font = {
              name: 'Calibri',
              family: 2,
              bold: true,
              size: 11,
              color: { argb: 'FFFFFF' },
            }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'fd8426' },
            }
          }
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
          }
          cell.alignment = {
            horizontal: 'center',
            vertical: 'center',
          }
        })
      })

      // Ajuste automático de largura das colunas
      worksheet.columns.forEach(column => {
        let dataMax = 0
        column.eachCell({ includeEmpty: true }, cell => {
          const columnLength = cell.value ? cell.value.toString().length : 15
          if (columnLength > dataMax) {
            dataMax = columnLength + 5
          }
        })
        column.width = dataMax < 15 ? 15 : dataMax
      })

      let blob = null
      await workbook.xlsx.writeBuffer().then(data => {
        const status = this.protestos[this.table].title
        const hoje = new Date().toISOString().slice(0, 10)
        blob = new File([data], `protestos_${status}_${hoje}.xlsx`, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      })

      return blob
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
